/*
 * Copyright © 2026 MS IMAGES. All rights reserved.
 * Proprietary and confidential — unauthorized copying, distribution, or use
 * of this file, via any medium, is strictly prohibited. See LICENSE.
 */

/**
 * SingleModuleSidebar — fallback when only 1 module is available (no switcher).
 * Renders a plain 220px fixed sidebar with module header, nav content, and user footer.
 */

import { AppVersion } from '@/components/ui/AppVersion';
import { useTranslation } from 'react-i18next';
import { NavLink } from 'react-router-dom';
import { useTheme } from '@/lib/theme';
import { projectConfig } from '@/config/project.config';
import type { ModuleDefinition } from '@/config/module-registry';

interface Props {
  activeModule: ModuleDefinition;
  children: React.ReactNode;
}

export function SingleModuleSidebar({ activeModule, children }: Props) {
  const { t } = useTranslation('common');
  const { isDark } = useTheme();
  const logo = isDark ? activeModule.logos.dark : activeModule.logos.light;

  return (
    <aside className="ms-single-sidebar">
      {/* Header */}
      <div className="ms-single-header">
        <div className="ms-single-brand">
          <div className="ms-single-logo">
            <img src={logo} alt={t(activeModule.nameKey)} />
          </div>
          <div>
            <div className="ms-single-title">{t(activeModule.nameKey)}</div>
            <div className="ms-single-subtitle">{t(activeModule.subtitleKey)}</div>
          </div>
        </div>
      </div>

      {/* Nav content */}
      <div className="ms-single-nav">{children}</div>

      {/* Footer */}
      <div className="ms-single-footer">
        <div className="ms-single-legal">
          <span>&copy; 2026 CoreX-Suite</span>
          <span className="ms-single-legal-sep">&middot;</span>
          <NavLink to="/privacy" className="ms-single-legal-link">{t('core:sidebar.privacy', 'Privacy')}</NavLink>
          <span className="ms-single-legal-sep">&middot;</span>
          <NavLink to="/terms" className="ms-single-legal-link">{t('core:sidebar.terms', 'Terms')}</NavLink>
          <span className="ms-single-legal-sep">&middot;</span>
          <AppVersion />
        </div>
      </div>

      <style>{styles}</style>
    </aside>
  );
}

const styles = `
  .ms-single-sidebar {
    --module-shell-width: 220px;
    position: fixed;
    top: 0;
    left: 0;
    width: 220px;
    height: 100vh;
    background: var(--color-bg-secondary);
    border-right: 1px solid var(--color-border-subtle);
    display: flex;
    flex-direction: column;
    z-index: 100;
  }

  .ms-single-header {
    padding: 12px 16px;
    border-bottom: 1px solid var(--color-border-subtle);
    flex-shrink: 0;
  }

  .ms-single-brand {
    display: flex;
    align-items: center;
    gap: 12px;
  }

  .ms-single-logo {
    width: 32px;
    height: 32px;
    border-radius: 6px;
    display: flex;
    align-items: center;
    justify-content: center;
    flex-shrink: 0;
  }

  .ms-single-logo img {
    width: 100%;
    height: 100%;
    object-fit: contain;
  }

  .ms-single-title {
    font-family: var(--font-heading);
    font-size: 13px;
    font-weight: 600;
    color: var(--color-text-primary);
  }

  .ms-single-subtitle {
    font-size: 10px;
    color: var(--color-text-secondary);
  }

  .ms-single-nav {
    flex: 1;
    overflow-y: auto;
    padding: 4px 8px;
  }

  .ms-single-footer {
    border-top: 1px solid var(--color-border-subtle);
    padding: 0;
    display: flex;
    flex-direction: column;
    flex-shrink: 0;
  }

  .ms-single-legal {
    display: flex;
    align-items: center;
    flex-wrap: wrap;
    padding: 6px 12px;
    font-size: 0.6rem;
    color: var(--color-text-muted);
  }

  .ms-single-legal-sep {
    margin: 0 4px;
    color: var(--color-border-subtle);
  }

  .ms-single-legal-link {
    color: var(--color-text-muted);
    text-decoration: none;
    font-size: 0.6rem;
  }

  .ms-single-legal-link:hover {
    color: var(--color-text-secondary);
  }

  @media (max-width: 768px) {
    .ms-single-sidebar {
      transform: translateX(-100%);
      transition: transform 0.3s ease;
    }
  }
`;
